// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { UseMutationResult, useMutation } from '@tanstack/react-query';
import { Holding } from '@daml.js/splice-api-token-holding-v1-1.0.0/lib/Splice/Api/Token/HoldingV1';
import { BridgeUserAgreement } from '@daml.js/utility-bridge-v0/lib/Utility/Bridge/V0/Agreement/User/module';
import { DepositAttestation } from '@daml.js/utility-bridge-v0/lib/Utility/Bridge/V0/Attestation/Deposit/module';
import { useLedgerApiClient } from '../../../../contexts/LedgerApiContext';
import { ContractId } from '@daml/types';
import getContextContracts from '../../../queries/bridge/useContextContracts';
import TemplateContract from '../../../../utils/TemplateContract';
import { useUtilityRegistryApiClient } from '../../../../contexts/UtilityApiContext';
import { useParties } from '../../../../contexts/PartiesContext';

const useMintFromAttestation: () => UseMutationResult<
  [ContractId<Holding>, string] | undefined,
  string,
  {
    bridgeUserAgreementCid: ContractId<BridgeUserAgreement>;
    depositAttestation: TemplateContract<DepositAttestation>;
  }
> = () => {
  const ledgerApi = useLedgerApiClient();
  const utilityRegistryApi = useUtilityRegistryApiClient();
  const { crossChainRepresentative } = useParties();

  return useMutation({
    mutationKey: ['mintFromAttestation', ledgerApi],
    mutationFn: async ({
      bridgeUserAgreementCid,
      depositAttestation,
    }) => {
      const output = {
        owner: depositAttestation.payload.recipient,
        amount: depositAttestation.payload.amount,
      };

      const contextContracts = await getContextContracts(
        utilityRegistryApi,
        crossChainRepresentative,
        [],
        [output]
      );

      return ledgerApi.mintFromAttestation(
        bridgeUserAgreementCid,
        depositAttestation.contractId,
        contextContracts.factoryCid,
        contextContracts.contextContractIds,
        contextContracts.requiredDisclosedContracts,
      );
    },
  });
};

export default useMintFromAttestation;
